import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { App, Button, Card, Input, Select, Space, Spin, Empty, Typography } from 'antd';
import {
  PlusOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  DeleteOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { PageHeader } from 'components/headings';
import {
  createReporteVisita,
  getReporteVisita,
  updateReporteVisita,
  cerrarReporteVisita,
} from 'api/reporteVisita';
import { captureGps } from 'hooks/useVisita';
import { getClientsApi } from 'api/client';
import { createBlock, moveBlock, removeBlock, updateBlock } from './visitaBlocks';
import AgregarBloqueModal from './AgregarBloqueModal';
import BloqueText from './blocks/BloqueText';
import BloqueChecklist from './blocks/BloqueChecklist';
import BloqueFirma from './blocks/BloqueFirma';

const { Text } = Typography;

const TIPOS = [
  { value: 'levantamiento', label: 'Levantamiento' },
  { value: 'diagnostico', label: 'Diagnóstico' },
  { value: 'preinspeccion', label: 'Preinspección' },
  { value: 'cierre', label: 'Cierre' },
  { value: 'libre', label: 'Libre' },
];

const BLOCK_COMPONENT = { text: BloqueText, checklist: BloqueChecklist, signatures: BloqueFirma };
const AUTOSAVE_MS = 2000;

export default function VisitaEditorPage() {
  const { id: idParam } = useParams();
  const navigate = useNavigate();
  const { message, modal } = App.useApp();

  const [loading, setLoading] = useState(true);
  const [reporteId, setReporteId] = useState(idParam ? Number(idParam) : null);
  const [titulo, setTitulo] = useState('Visita en terreno');
  const [tipo, setTipo] = useState('libre');
  const [secciones, setSecciones] = useState([]);
  const [estado, setEstado] = useState('draft');
  const [idCliente, setIdCliente] = useState(null);
  const [clienteNoReg, setClienteNoReg] = useState('');
  const [clientes, setClientes] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const createdRef = useRef(false);
  const saveTimer = useRef(null);

  // Catálogo de clientes registrados para el selector.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await getClientsApi();
      const list = Array.isArray(data) ? data : data?.clients || data?.rows || [];
      if (!cancelled) setClientes(list.filter((c) => c && c.id != null));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Patch del cliente: registrado pisa al no-registrado y viceversa.
  const clientePatch = () =>
    idCliente
      ? { id_cliente: idCliente, cliente_no_registrado: null }
      : clienteNoReg.trim()
      ? { id_cliente: null, cliente_no_registrado: { nombre: clienteNoReg.trim() } }
      : { id_cliente: null, cliente_no_registrado: null };

  // Carga (modo /:id) o creación inicial (modo /nueva) con GPS de apertura.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (idParam) {
        try {
          const r = await getReporteVisita(Number(idParam));
          if (cancelled || !r) return;
          setReporteId(r.id);
          setTitulo(r.titulo);
          setTipo(r.tipo);
          setSecciones(Array.isArray(r.secciones) ? r.secciones : []);
          setEstado(r.estado);
          setIdCliente(r.id_cliente ?? null);
          setClienteNoReg(r.cliente_no_registrado?.nombre || '');
        } catch {
          message.error('No se pudo cargar el reporte');
        } finally {
          if (!cancelled) setLoading(false);
        }
      } else {
        if (createdRef.current) return;
        createdRef.current = true;
        const gps = await captureGps();
        try {
          const r = await createReporteVisita({
            tipo: 'libre',
            titulo: 'Visita en terreno',
            secciones: [],
            gps_apertura: gps,
          });
          if (cancelled) return;
          setReporteId(r.id);
          setEstado(r.estado);
          setLoading(false);
          navigate(`/specialist/visitas/${r.id}`, { replace: true });
        } catch {
          message.error('No se pudo crear la visita');
          if (!cancelled) setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idParam]);

  // Autosave online debounced. Solo en draft.
  const scheduleSave = useCallback(
    (patch) => {
      if (!reporteId || estado !== 'draft') return;
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(async () => {
        try {
          await updateReporteVisita(reporteId, patch);
          setSavedAt(new Date());
        } catch {
          /* best-effort: el próximo cambio reintenta */
        }
      }, AUTOSAVE_MS);
    },
    [reporteId, estado]
  );

  useEffect(() => {
    if (loading || !reporteId) return;
    scheduleSave({ titulo, tipo, secciones, ...clientePatch() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titulo, tipo, secciones, idCliente, clienteNoReg]);

  const handleBlockChange = (blockId, patch) =>
    setSecciones((prev) => updateBlock(prev, blockId, patch));
  const addBlock = (type) => {
    setSecciones((prev) => [...prev, createBlock(type)]);
    setAddOpen(false);
  };

  const cerrar = () => {
    modal.confirm({
      title: 'Cerrar visita',
      content: 'Una vez cerrada, el reporte no se puede editar. ¿Confirmás?',
      okText: 'Cerrar visita',
      cancelText: 'Cancelar',
      onOk: async () => {
        const gps = await captureGps();
        try {
          await cerrarReporteVisita(reporteId, { gps_cierre: gps });
          message.success('Visita cerrada');
          navigate('/specialist/visitas');
        } catch (e) {
          message.error(e?.response?.data?.message || 'No se pudo cerrar la visita');
        }
      },
    });
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin />
      </div>
    );
  }

  const readonly = estado !== 'draft';

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: 12 }}>
      <PageHeader
        title="Reporte de visita"
        subtitle={
          savedAt ? `Guardado ${savedAt.toLocaleTimeString('es-CL')}` : 'Autoguardado activo'
        }
      />
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Input
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          placeholder="Título de la visita"
          disabled={readonly}
        />
        <Select
          value={tipo}
          onChange={setTipo}
          options={TIPOS}
          style={{ width: '100%' }}
          disabled={readonly}
        />

        <Select
          showSearch
          allowClear
          placeholder="Cliente registrado"
          value={idCliente ?? undefined}
          onChange={(v) => {
            setIdCliente(v ?? null);
            if (v) setClienteNoReg('');
          }}
          options={clientes.map((c) => ({ value: c.id, label: c.name }))}
          filterOption={(input, opt) =>
            (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          style={{ width: '100%' }}
          disabled={readonly}
        />
        <Input
          placeholder="…o nombre de cliente no registrado (levantamiento)"
          value={clienteNoReg}
          onChange={(e) => {
            setClienteNoReg(e.target.value);
            if (e.target.value) setIdCliente(null);
          }}
          disabled={readonly || !!idCliente}
        />

        {secciones.length === 0 ? (
          <Empty description="Sin bloques todavía. Agregá el primero." />
        ) : null}

        {secciones.map((block, i) => {
          const Cmp = BLOCK_COMPONENT[block.type];
          return (
            <Card
              key={block.id}
              size="small"
              title={
                <Input
                  variant="borderless"
                  value={block.title}
                  onChange={(e) => handleBlockChange(block.id, { title: e.target.value })}
                  disabled={readonly}
                />
              }
              extra={
                !readonly && (
                  <Space>
                    <Button
                      size="small"
                      icon={<ArrowUpOutlined />}
                      disabled={i === 0}
                      onClick={() => setSecciones(moveBlock(secciones, i, -1))}
                    />
                    <Button
                      size="small"
                      icon={<ArrowDownOutlined />}
                      disabled={i === secciones.length - 1}
                      onClick={() => setSecciones(moveBlock(secciones, i, 1))}
                    />
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => setSecciones(removeBlock(secciones, block.id))}
                    />
                  </Space>
                )
              }
            >
              {Cmp ? (
                <Cmp block={block} onChange={(patch) => handleBlockChange(block.id, patch)} />
              ) : (
                <Text type="secondary">Bloque no soportado: {block.type}</Text>
              )}
            </Card>
          );
        })}

        {!readonly && (
          <>
            <Button icon={<PlusOutlined />} onClick={() => setAddOpen(true)} block>
              Agregar bloque
            </Button>
            <Button type="primary" icon={<CheckOutlined />} onClick={cerrar} block size="large">
              Cerrar visita
            </Button>
          </>
        )}
      </Space>

      <AgregarBloqueModal open={addOpen} onPick={addBlock} onClose={() => setAddOpen(false)} />
    </div>
  );
}
